import React, { useMemo } from 'react';
import { FloatingPanel, PanelSection } from '../ui/Panel';
import { Button } from '../ui/Button';
import { ARENA_PANEL_PRODUCTS, findPanelProduct } from '../../constants/arenaPanels';
import { summarizePanels } from '../../utils/panelLayout';
import { usePanelMaxHeight } from '../ui/usePanelMaxHeight';

const muted = { color: '#6080a0', fontSize: '11px', lineHeight: 1.5 };
const selectStyle = {
  width: '100%', padding: '7px', marginBottom: '8px', background: '#1a2030', color: '#fff',
  border: '2px solid rgba(0,200,255,0.3)', borderRadius: '6px', fontSize: '12px',
};
const money = (v) => `$${Math.round(v).toLocaleString()}`;

/**
 * ArenaPanelsPanel - pick a brand's corral/arena panels and gates, place them
 * by hand or fence an outline automatically, and total up what to buy.
 */
export const ArenaPanelsPanel = ({
  choice,
  onChangeChoice,
  placing,
  onTogglePlacing,
  rooms,
  fence,
  onChangeFence,
  onFenceOutline,
  fenceReport,
  onFitOutline,
  canFitOutline,
  panels,
  onClearFence,
  onClearAll,
  onShow3D,
  onClose,
  isMobile = false,
}) => {
  const [measureRef, maxHeight] = usePanelMaxHeight();
  const product = findPanelProduct(choice.productId) || ARENA_PANEL_PRODUCTS[0];
  const panelOptions = product.options.filter(o => o.kind === 'panel');
  const gateOptions = product.options.filter(o => o.kind === 'gate');
  const outlineRooms = rooms.filter(r => r.points?.length >= 3);
  const outline = outlineRooms.find(r => r.id === fence.roomId);
  const brands = [...new Set(ARENA_PANEL_PRODUCTS.map(p => p.brand))];

  const summary = useMemo(() => summarizePanels(panels, findPanelProduct), [panels]);
  const totalPrice = summary.every(r => r.unitPrice != null)
    ? summary.reduce((s, r) => s + r.unitPrice * r.count, 0)
    : null;
  const totalFeet = summary.reduce((s, r) => s + r.lengthFt * r.count, 0);
  const fenceCount = outline ? panels.filter(p => p.fenceId === outline.id).length : 0;

  const setProduct = (productId) => {
    const next = findPanelProduct(productId);
    const firstPanel = next.options.find(o => o.kind === 'panel');
    onChangeChoice({ productId, optionLabel: firstPanel?.label });
    onChangeFence({
      ...fence,
      panelLabel: firstPanel?.label,
      gates: fence.gates.map(g => ({ ...g, label: next.options.find(o => o.kind === 'gate')?.label })),
    });
  };

  const sideLabel = (i) => {
    const pts = outline?.points;
    if (!pts) return `Side ${i + 1}`;
    const a = pts[i], b = pts[(i + 1) % pts.length];
    return `Side ${i + 1} (${Math.round(Math.hypot(b.x - a.x, b.y - a.y) / 40)} ft)`;
  };

  return (
    <FloatingPanel
      title="Arena Panels"
      onClose={onClose}
      x={isMobile ? 10 : 100}
      y={isMobile ? 60 : 80}
      width={isMobile ? 300 : 340}
      maxHeight={maxHeight}
      isMobile={isMobile}
    >
      <div ref={measureRef} style={{ padding: '12px 14px' }}>
        <Button variant="primary" onClick={onShow3D} style={{ width: '100%', marginBottom: '12px' }}>View in 3D</Button>
        <PanelSection title="Product">
          <select style={selectStyle} value={product.id} onChange={(e) => setProduct(e.target.value)}>
            {brands.map(brand => (
              <optgroup key={brand} label={brand}>
                {ARENA_PANEL_PRODUCTS.filter(p => p.brand === brand).map(p => (
                  <option key={p.id} value={p.id}>{p.line}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <div style={{ ...muted, color: '#a0b0c0' }}>
            {product.heightIn ? `${Math.floor(product.heightIn / 12)}'${product.heightIn % 12 ? ` ${product.heightIn % 12}"` : ''} tall · ` : ''}
            {product.rails ? `${product.rails} rails · ` : ''}
            {product.tube || ''}
            {product.connection ? <div>Connects with: {product.connection}</div> : null}
            {product.notes ? <div style={muted}>{product.notes}</div> : null}
            {product.sourceUrl && (
              <div><a href={product.sourceUrl} target="_blank" rel="noreferrer" style={{ color: '#00c8ff' }}>Manufacturer specs</a></div>
            )}
          </div>
        </PanelSection>

        <PanelSection title="Place by hand">
          <select style={selectStyle} value={choice.optionLabel || ''}
            onChange={(e) => onChangeChoice({ ...choice, productId: product.id, optionLabel: e.target.value })}>
            {product.options.map(o => <option key={o.label} value={o.label}>{o.label}</option>)}
          </select>
          <Button variant={placing ? 'primary' : 'default'} onClick={onTogglePlacing} style={{ width: '100%', fontSize: '11px' }}>
            {placing ? 'Placing… (Esc to finish a run)' : 'Place panels'}
          </Button>
          <p style={muted}>
            Click to start, then each click drops one panel toward the cursor, pinned end to end. Hold Shift for any
            angle. Drag placed panels with the Select tool; their ends pin to nearby panels.
          </p>
        </PanelSection>

        <PanelSection title="Fence an outline">
          {outlineRooms.length === 0 ? (
            <p style={muted}>Draw the arena outline first (Room tool, or Site Grading → Add arena outline).</p>
          ) : (
            <>
              <select style={selectStyle} value={fence.roomId || ''} onChange={(e) => onChangeFence({ ...fence, roomId: e.target.value || null })}>
                <option value="">Choose an outline…</option>
                {outlineRooms.map(r => <option key={r.id} value={r.id}>{r.name || 'Room'}</option>)}
              </select>
              <select style={selectStyle} value={fence.panelLabel || ''} onChange={(e) => onChangeFence({ ...fence, panelLabel: e.target.value })}>
                {panelOptions.map(o => <option key={o.label} value={o.label}>{o.label}</option>)}
              </select>

              <div style={{ ...muted, marginBottom: '4px' }}>Gates (placed near the middle of their side):</div>
              {fence.gates.map((gate, i) => (
                <div key={i} style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <select style={{ ...selectStyle, flex: 1 }} value={gate.side}
                    onChange={(e) => onChangeFence({ ...fence, gates: fence.gates.map((g, k) => k === i ? { ...g, side: Number(e.target.value) } : g) })}>
                    {(outline?.points || [0, 0, 0, 0]).map((_, s) => <option key={s} value={s}>{sideLabel(s)}</option>)}
                  </select>
                  <select style={{ ...selectStyle, flex: 1.4 }} value={gate.label || ''}
                    onChange={(e) => onChangeFence({ ...fence, gates: fence.gates.map((g, k) => k === i ? { ...g, label: e.target.value } : g) })}>
                    {gateOptions.map(o => <option key={o.label} value={o.label}>{o.label}</option>)}
                  </select>
                  <button onClick={() => onChangeFence({ ...fence, gates: fence.gates.filter((_, k) => k !== i) })}
                    style={{ background: 'none', border: 'none', color: '#ff6666', cursor: 'pointer', marginBottom: '8px' }} title="Remove gate">✕</button>
                </div>
              ))}
              {gateOptions.length > 0 && (
                <Button variant="default" style={{ width: '100%', fontSize: '11px', marginBottom: '8px' }}
                  onClick={() => onChangeFence({ ...fence, gates: [...fence.gates, { side: 0, label: gateOptions[0].label }] })}>
                  + Add gate
                </Button>
              )}

              <Button variant="primary" disabled={!outline} onClick={onFenceOutline} style={{ width: '100%', fontSize: '11px', marginBottom: '6px' }}>
                Fence this outline
              </Button>
              {canFitOutline && (
                <Button variant="default" onClick={onFitOutline} style={{ width: '100%', fontSize: '11px', marginBottom: '6px' }}>
                  Resize arena to whole panels
                </Button>
              )}
              <p style={muted}>Fencing again replaces the panels from the last fence of this outline.</p>

              {fenceReport && (
                <div style={{ ...muted, color: '#a0b0c0' }}>
                  {fenceReport.map(s => (
                    <div key={s.side}>
                      {sideLabel(s.side)}: {s.panels} panel{s.panels === 1 ? '' : 's'}{s.gates ? ` + ${s.gates} gate` : ''}
                      {Math.abs(s.mismatchFt) >= 0.25 && (
                        <span style={{ color: '#ffaa00' }}>
                          {' '}· {Math.abs(s.mismatchFt).toFixed(1)} ft {s.mismatchFt > 0 ? 'of gaps' : 'too much panel'}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </PanelSection>

        <PanelSection title="Parts list">
          {summary.length === 0 ? (
            <p style={muted}>No panels placed yet.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', color: '#c0d0e0' }}>
              <tbody>
                {summary.map(row => (
                  <tr key={row.key}>
                    <td style={{ padding: '3px 2px' }}>
                      <div>{row.product ? `${row.product.brand} ${row.product.line}` : row.label}</div>
                      <div style={{ color: '#6080a0' }}>{row.label}</div>
                    </td>
                    <td style={{ textAlign: 'right' }}>×{row.count}</td>
                    <td style={{ textAlign: 'right', color: '#6080a0' }}>{row.unitPrice != null ? money(row.unitPrice * row.count) : '—'}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '1px solid rgba(255,255,255,0.1)', color: '#00c8ff' }}>
                  <td style={{ padding: '4px 2px' }}>{totalFeet.toLocaleString()} ft of fence</td>
                  <td />
                  <td style={{ textAlign: 'right' }}>{totalPrice != null ? money(totalPrice) : ''}</td>
                </tr>
              </tbody>
            </table>
          )}
          <p style={muted}>Prices are typical retail seen online and change often. Check with your dealer.</p>
          {panels.length > 0 && (
            <div style={{ display: 'flex', gap: '6px' }}>
              <Button variant="default" disabled={!fenceCount} onClick={onClearFence}
                style={{ flex: 1, fontSize: '11px' }}
                title={outline ? `Remove the ${fenceCount} panels fenced around ${outline.name || 'this outline'}` : 'Choose an outline above'}>
                Clear {outline?.name || 'outline'} fence
              </Button>
              <Button variant="danger" onClick={onClearAll} style={{ flex: 1, fontSize: '11px' }}>
                Clear all panels
              </Button>
            </div>
          )}
          {panels.length > 0 && <p style={{ ...muted, marginTop: '6px' }}>Ctrl+Z brings cleared panels back.</p>}
        </PanelSection>
      </div>
    </FloatingPanel>
  );
};

export default ArenaPanelsPanel;
